import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "../../../../utils/appToast";
import { CreditCard, Loader2, RefreshCw, Clock } from "lucide-react";
import {
  getBillingBalance, getBillingPlans, createPlanCheckout, createAddonCheckout,
  createBillingPortalSession, changeBillingPlan, cancelBillingSubscription,
} from "../../extractorApi";
import { SX, TX } from "../studioTokens";
import { SectionHead, Banner } from "./BillingBits";
import SubscriptionHero from "./SubscriptionHero";
import StatusBanners from "./StatusBanners";
import PlanCards from "./PlanCards";
import CreditAddOns from "./CreditAddOns";
import TransactionHistory from "./TransactionHistory";
import BillingConfirm from "./BillingConfirm";
import { TRIAL_PLAN_KEY, billingErrMsg, normalizePlan, normalizeAddon, fmtDate } from "./billingConst";

/* Landing back on success_url only proves the browser navigation worked — the
   credits are granted seconds later, when Stripe calls the backend's webhook.
   So: read the balance once, and if it hasn't moved, retry exactly once after
   a short delay before showing a "still processing" state. Deliberately not a
   poll loop (Phase 4 of the Stripe integration guide). */
const SETTLE_DELAY_MS = 2000;

const BillingPage = () => {
  const [balance, setBalance] = useState(null);
  const [catalog, setCatalog] = useState({ plans: [], addons: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(null);        // action key, disables every other control
  const [intent, setIntent] = useState(null);    // pending confirm-modal action
  const [settling, setSettling] = useState(false);
  const [stalled, setStalled] = useState(false); // webhook still hadn't landed after the retry

  const plansRef = useRef(null);
  const settleTimer = useRef(null);

  const refreshBalance = useCallback(async () => {
    const data = await getBillingBalance();
    setBalance(data);
    return data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setStalled(false);
    try {
      const [bal, cat] = await Promise.all([getBillingBalance(), getBillingPlans()]);
      setBalance(bal);
      // The catalog is served by two backend generations with different field
      // names — normalise before anything downstream reads it.
      setCatalog({
        plans: (cat?.plans || []).map(normalizePlan),
        addons: (cat?.addons || []).map(normalizeAddon),
      });
    } catch (err) {
      setLoadError(billingErrMsg(err, "Couldn't load your billing details."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stopSettling = useCallback(() => {
    if (settleTimer.current) { clearTimeout(settleTimer.current); settleTimer.current = null; }
    setSettling(false);
  }, []);

  /* Read now; if the webhook hasn't landed, read once more after a beat. */
  const settle = useCallback(async (before) => {
    stopSettling();
    setStalled(false);
    setSettling(true);
    const moved = (next) =>
      next?.plan_key !== before?.plan_key ||
      Number(next?.balance) !== Number(before?.balance) ||
      next?.status !== before?.status;

    try {
      if (moved(await refreshBalance())) { setSettling(false); return; }
    } catch { /* fall through to the single retry */ }

    settleTimer.current = setTimeout(async () => {
      settleTimer.current = null;
      try {
        if (!moved(await refreshBalance())) setStalled(true);
      } catch {
        setStalled(true);
      } finally {
        setSettling(false);
      }
    }, SETTLE_DELAY_MS);
  }, [refreshBalance, stopSettling]);

  useEffect(() => () => stopSettling(), [stopSettling]);

  /* Coming back from Stripe: the webhook that grants credits may land a beat
     after the redirect, so poll rather than trusting the first read. */
  useEffect(() => {
    if (loading || !balance) return;
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("billing");
    if (!outcome) return;
    params.delete("billing");
    const qs = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    if (outcome === "success") { toast.success("Payment received — updating your balance…"); settle(balance); }
    else if (outcome === "cancelled") toast.info("Checkout cancelled. Nothing was charged.");
  }, [loading, balance, settle]);

  const scrollToPlans = () => plansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  // ── Actions ───────────────────────────────────────────────────────────────
  const openPortal = async () => {
    setBusy("portal");
    try {
      const { portal_url: url } = await createBillingPortalSession();
      if (!url) throw new Error("no portal url");
      window.location.href = url;
    } catch (err) {
      setBusy(null);
      toast.error(billingErrMsg(err, "Couldn't open the billing portal. Try again in a moment."));
    }
  };

  const runIntent = async () => {
    if (!intent) return;
    const { mode, plan, addon } = intent;
    const key = mode === "addon" ? `addon:${addon.key}` : mode === "cancel" ? "cancel" : `plan:${plan.key}`;
    setBusy(key);
    try {
      if (mode === "checkout" || mode === "addon") {
        const res = mode === "checkout"
          ? await createPlanCheckout(plan.key)
          : await createAddonCheckout(addon.key);
        const url = res?.checkout_url;
        if (!url) throw new Error("no checkout url");
        window.location.href = url; // Stripe-hosted page — leave busy set through the redirect
        return;
      }

      if (mode === "change") {
        const before = balance;
        const res = await changeBillingPlan(plan.key);
        setIntent(null);
        if (res?.scheduled) {
          toast.success(`Switching to ${plan.name} on ${fmtDate(res.effective_at || before?.period_end)}.`);
          await refreshBalance();
        } else {
          toast.success(`Upgrading to ${plan.name} — confirming with Stripe…`);
          await refreshBalance();
          settle(before);
        }
      } else if (mode === "cancel") {
        const res = await cancelBillingSubscription();
        setIntent(null);
        toast.success(`Subscription cancelled. You keep access until ${fmtDate(res?.period_end || balance?.period_end)}.`);
        await refreshBalance();
      }
    } catch (err) {
      toast.error(billingErrMsg(err, "That billing action didn't go through. Please try again."));
    } finally {
      setBusy(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div>
        <div className="max-w-[1100px] mx-auto flex items-center justify-center gap-2 py-24 text-[13.5px] text-[#8A8A90]">
          <Loader2 size={17} className="animate-spin text-[#A04A97]" /> Loading your billing details…
        </div>
      </div>
    );
  }

  if (loadError || !balance) {
    return (
      <div>
        <div className={`max-w-[560px] mx-auto mt-16 ${SX.card} p-8 text-center`}>
          <CreditCard size={24} className="text-[#8A8A90] mx-auto mb-3" />
          <div className={TX.cardTitle}>Billing is unavailable right now</div>
          <p className={`${TX.helper} mt-2 leading-[1.55]`}>
            {loadError || "We couldn't read your subscription. Your credits are unaffected."}
          </p>
          <button className={`${SX.btn} ${SX.btnPrimary} mt-5 mx-auto`} onClick={load}>
            <RefreshCw size={14} /> Try again
          </button>
        </div>
      </div>
    );
  }

  const paidPlans = catalog.plans.filter((p) => p.key !== TRIAL_PLAN_KEY);
  const pendingPlan = catalog.plans.find((p) => p.key === balance.pending_plan_key);

  const firstAddon = catalog.addons[0];
  const buyFirstAddon = () => firstAddon && setIntent({ mode: "addon", addon: firstAddon });

  return (
    <div>
      <div className="max-w-[1100px] mx-auto flex flex-col gap-7 pb-10">
        {/* The page title and the RFP / Extractor Studio switch come from Settings > Billing. */}
        {stalled && (
          <Banner
            tone="info"
            icon={Clock}
            title="Still processing your payment…"
            actions={
              <button className={`${SX.btn} ${SX.btnSm} ${SX.btnSoft}`} onClick={() => { stopSettling(); load(); }}>
                Check again
              </button>
            }
          >
            Your payment went through, but Stripe hasn't confirmed the credit grant yet. It usually lands within a
            few seconds — nothing is lost if you leave and come back.
          </Banner>
        )}

        <StatusBanners
          balance={balance}
          planName={pendingPlan?.name || balance.pending_plan_key}
          activating={settling}
          onBuyCredits={buyFirstAddon}
          onViewPlans={scrollToPlans}
        />

        <SubscriptionHero
          balance={balance}
          nextPlan={balance.pending_plan_key !== balance.plan_key ? pendingPlan : null}
          busy={busy}
          onPortal={openPortal}
          onBuyCredits={buyFirstAddon}
          onViewPlans={scrollToPlans}
          onCancel={() => setIntent({ mode: "cancel", currentPlanName: balance.plan_name, periodEnd: balance.period_end })}
        />

        <div>
          <SectionHead
            title="Credit add-on packs"
            hint="One-off top-ups for when a busy month outruns your allocation."
          />
          <CreditAddOns
            addons={catalog.addons}
            locked={!balance.can_buy_addons}
            busy={busy}
            onBuy={(addon) => setIntent({ mode: "addon", addon })}
          />
        </div>

        <div ref={plansRef} className="scroll-mt-6">
          <SectionHead
            title="Plans &amp; pricing"
            hint="Every plan includes all extraction engines, full API access and all export formats."
          />
          <PlanCards
            plans={paidPlans}
            balance={balance}
            busy={busy}
            onCheckout={(plan) => setIntent({ mode: "checkout", plan })}
            onChange={(plan, transition) => setIntent({
              mode: "change", plan, transition,
              currentPlanName: balance.plan_name, periodEnd: balance.period_end,
            })}
          />
        </div>

        <div>
          <SectionHead title="Credit activity" hint="Every grant, purchase and spend against your balance." />
          <TransactionHistory />
        </div>
      </div>

      <BillingConfirm
        intent={intent}
        busy={!!busy}
        onClose={() => setIntent(null)}
        onConfirm={runIntent}
      />
    </div>
  );
};

export default BillingPage;
